"use client"
import React, { useRef } from "react"
import { Alert, Button, Form } from "react-bootstrap"
import styles from "./LoginForm.module.css"
import type { OAuthProvider } from "./auth"

interface LoginFormProps {
    email: string
    setEmail: (email: string) => void
    code: string
    setCode: (code: string) => void
    codeState: boolean
    setCodeState: (state: boolean) => void
    submit: boolean
    setSubmit: (submit: boolean) => void
    error: string
    setError: (error: string) => void
    notify: string
    setNotify: (notify: string) => void
    submitEmail: () => void
    submitCode: () => void
    oauthProviders: Record<string, OAuthProvider>
}

const PROVIDER_LABELS: Record<string, string> = {
    vk: "VK ID",
    yandex: "Яндекс",
}

const PROVIDER_STYLES: Record<string, { bg: string; color: string }> = {
    vk: { bg: "#0077FF", color: "#fff" },
    yandex: { bg: "#FC3F1D", color: "#fff" },
}

const PROVIDER_ICONS: Record<string, React.ReactNode> = {
    vk: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8, flexShrink: 0 }}>
            <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.136.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4 8.673 4 8.231c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z" />
        </svg>
    ),
    yandex: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8, flexShrink: 0 }}>
            <path d="M12.84 16.84V24h-3.36V5.6h5.88c1.44 0 2.56.36 3.36 1.08.8.72 1.2 1.68 1.2 2.88 0 1.28-.44 2.28-1.32 3-.88.72-2.08 1.08-3.6 1.08h-2.16v3.2zm0-5.76h2.16c.64 0 1.12-.16 1.44-.48.32-.32.48-.76.48-1.32 0-.56-.16-1-.48-1.32-.32-.32-.8-.48-1.44-.48h-2.16v3.6zM2.56 24V5.6h3.36v13.04h4.8V24H2.56z" />
        </svg>
    ),
}

export const LoginForm: React.FC<LoginFormProps> = ({
    email,
    setEmail,
    code,
    setCode,
    codeState,
    setCodeState,
    submit,
    setSubmit,
    error,
    setError,
    notify,
    setNotify,
    submitEmail,
    submitCode,
    oauthProviders,
}) => {
    const codeRef = useRef<HTMLInputElement>(null)

    React.useEffect(() => {
        if (codeState && codeRef.current) {
            codeRef.current.focus()
        }
    }, [codeState])

    return (
        <div className={styles.container}>
            <div className={styles.loginCard}>
                <div className={styles.logoSection}>
                    <img
                        src="/splash400.png"
                        alt="Artifacts"
                        className={styles.logo}
                        style={{ width: 400, height: 400, objectFit: 'contain' }}
                    />
                </div>

                {error && <Alert variant="danger" className={styles.alert}>{error}</Alert>}
                {notify && <Alert variant="info" className={styles.alert}>{notify}</Alert>}

                <Form className={styles.form}>
                    <p className={styles.instruction}>Введите адрес почты чтобы войти в сервис</p>

                    <Form.Group className={styles.formGroup}>
                        <Form.Control
                            ref={codeRef}
                            disabled={codeState || submit}
                            type="email"
                            placeholder="example@mail.ru"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && submitEmail()}
                            className={styles.input}
                        />
                    </Form.Group>

                    {!codeState ? (
                        <Button
                            variant="primary"
                            onClick={submitEmail}
                            disabled={submit || !email}
                            className={styles.button}
                        >
                            {submit ? "Отправка..." : "Далее"}
                        </Button>
                    ) : (
                        <>
                            <Form.Group className={styles.formGroup}>
                                <Form.Label className={styles.label}>Проверочный код из почты</Form.Label>
                                <Form.Control
                                    ref={codeRef}
                                    disabled={submit}
                                    placeholder="Введите код"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && submitCode()}
                                    className={styles.input}
                                />
                            </Form.Group>
                            <Button
                                variant="primary"
                                onClick={submitCode}
                                disabled={submit}
                                className={styles.button}
                            >
                                {submit ? "Отправка..." : "Отправить код"}
                            </Button>
                        </>
                    )}

                    {Object.keys(oauthProviders).length > 0 && (
                        <>
                            <div className={styles.divider}>
                                <span className={styles.dividerText}>или</span>
                            </div>
                            <div className={styles.oauthButtons}>
                                {Object.entries(oauthProviders).map(([key, provider]) => {
                                    const style = PROVIDER_STYLES[key] || { bg: "#333", color: "#fff" }
                                    return (
                                        <a
                                            key={key}
                                            href={provider.auth_url}
                                            className={styles.oauthButton}
                                            style={{
                                                backgroundColor: style.bg,
                                                color: style.color,
                                            }}
                                        >
                                            {PROVIDER_ICONS[key]}
                                            {PROVIDER_LABELS[key] || provider.name || key}
                                        </a>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </Form>
            </div>
        </div>
    )
}
